import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useField } from "react-final-form";

import { Card, PrimaryButton, Stack, Text } from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { ActivityDescription } from "@stores/clinical/models/ActivityDescription.ts";
import { CheckboxField } from "@ui-components/form/CheckboxField.tsx";
import { SpinNumberInputField } from "@ui-components/form/SpinNumberInputField.tsx";
import { usePatientLabel } from "@ui-components/hooks/usePatientLabel.ts";
import { When } from "@ui-components/withPerm.tsx";

import { ClinicalReminderList } from "./ClinicalReminderList.tsx";
import { ClinicalReminderPreferenceDialog } from "./ClinicalReminderPreferenceDialog.tsx";
import {
  ClinicalReminderPreferenceFormValues,
  nameOfClinicalReminderPreferences as nameOf
} from "./ClinicalReminderPreferenceForm.types.ts";

export const ClinicalReminderFields: React.FC = observer(() => {
  const [
    showClinicalReminderPreferenceDialog,
    setShowClinicalReminderPreferenceDialog
  ] = useState<boolean>(false);

  const [selectedClinicalReminder, setSelectedClinicalReminder] = useState<
    ActivityDescription | undefined
  >(undefined);

  const {
    input: { value: useReminderMinRange }
  } = useField<ClinicalReminderPreferenceFormValues["useReminderMinRange"]>(
    nameOf("useReminderMinRange"),
    {
      subscription: { value: true }
    }
  );

  const label = usePatientLabel();

  return (
    <>
      <Card
        iconName="ClipboardList"
        headingLevel="section-heading"
        heading="Clinical reminders"
        button={
          <When permission={Permission.ActivityDescriptionWrite}>
            <PrimaryButton
              iconProps={{ iconName: "Add" }}
              text="Add clinical reminder"
              onClick={() => setShowClinicalReminderPreferenceDialog(true)}
            />
          </When>
        }
      >
        <Stack tokens={{ childrenGap: 24 }}>
          <Stack tokens={{ childrenGap: 8 }}>
            <CheckboxField
              label="Allow free-text reminders"
              name={nameOf("allowReminderFreeText")}
            />
            <When permission={Permission.ClinicalReminderFeatureToggle}>
              <Stack tokens={{ childrenGap: 8 }}>
                <CheckboxField
                  name={nameOf("useReminderMinRange")}
                  label={`Hide by default & don't process reminders for ${label}s with appointment booked within the next`}
                />
                <Stack
                  horizontal
                  verticalAlign="center"
                  tokens={{ childrenGap: 8 }}
                  styles={{ root: { paddingLeft: 28 } }}
                >
                  <SpinNumberInputField
                    styles={{ root: { width: 50 } }}
                    name={nameOf("reminderMinWeekRange")}
                    disabled={!useReminderMinRange}
                    min={1}
                  />
                  <Text bold>weeks</Text>
                </Stack>
              </Stack>
            </When>
          </Stack>
          <ClinicalReminderList
            setShowClinicalReminderPreferenceDialog={
              setShowClinicalReminderPreferenceDialog
            }
            setSelectedClinicalReminder={setSelectedClinicalReminder}
          />
        </Stack>
      </Card>
      {showClinicalReminderPreferenceDialog && (
        <ClinicalReminderPreferenceDialog
          selectedActivityDescription={selectedClinicalReminder}
          onDismiss={() => {
            setShowClinicalReminderPreferenceDialog(false);
            setSelectedClinicalReminder(undefined);
          }}
        />
      )}
    </>
  );
});
